import { useEffect } from 'react'

/**
 * Per-route `<title>`, description, canonical, hreflang and Open Graph.
 *
 * The site is an SEO engine (Docs/07 §9), and every tag below is on that
 * checklist. It is still applied in the browser rather than at render time —
 * the app is a SPA — so a crawler that does not execute JavaScript sees the
 * document defaults. Pre-rendering the directory and profile routes is the next
 * step and is listed as outstanding, not silently assumed to be done.
 *
 * `noIndex` matters more than the rest: a directory URL for a category that does
 * not exist must not be indexed, and a SPA cannot answer 404 with a status code.
 */

export type DocumentMeta = {
  title: string
  description?: string
  /** Absolute or root-relative. Defaults to the current path. */
  canonicalPath?: string
  /** `hreflang` → path. Emitted as `<link rel="alternate">`. */
  alternates?: Record<string, string>
  /** True on a 404 or any thin page that must stay out of the index. */
  noIndex?: boolean
  /** `website` (default) or `article`. */
  ogType?: string
  ogImage?: string
}

const MANAGED = 'data-avexa-meta'

const absolute = (path: string): string =>
  path.startsWith('http') ? path : new URL(path, window.location.origin).toString()

const upsertMeta = (attribute: 'name' | 'property', key: string, content: string): void => {
  const selector = `meta[${attribute}="${key}"]`
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    element.setAttribute(MANAGED, '')
    document.head.appendChild(element)
  }
  element.content = content
}

const upsertLink = (rel: string, href: string, hreflang?: string): void => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`
  let element = document.head.querySelector<HTMLLinkElement>(selector)
  if (!element) {
    element = document.createElement('link')
    element.rel = rel
    if (hreflang) element.hreflang = hreflang
    element.setAttribute(MANAGED, '')
    document.head.appendChild(element)
  }
  element.href = href
}

/**
 * Clears EVERY `rel="alternate"`, including the ones `index.html` ships.
 *
 * Those static links describe the homepage. Leaving them in place on a
 * directory route would give the document two different `hreflang="nb-NO"`
 * URLs, which is worse than having none: Google discards a conflicting cluster
 * entirely. So this hook owns the whole set, and a route that has translated
 * URLs declares them.
 */
const removeAlternates = (): void => {
  for (const element of document.head.querySelectorAll('link[rel="alternate"]')) {
    element.remove()
  }
}

export const useDocumentMeta = ({
  title,
  description,
  canonicalPath,
  alternates,
  noIndex = false,
  ogType = 'website',
  ogImage,
}: DocumentMeta): void => {
  // The alternates object is rebuilt on every render, so it is serialised into
  // the dependency list rather than compared by reference.
  const alternatesKey = alternates ? JSON.stringify(alternates) : ''

  useEffect(() => {
    document.title = title

    const canonical = absolute(canonicalPath ?? window.location.pathname)
    upsertLink('canonical', canonical)

    upsertMeta('name', 'robots', noIndex ? 'noindex, follow' : 'index, follow')
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:type', ogType)
    upsertMeta('property', 'og:url', canonical)
    upsertMeta('name', 'twitter:card', ogImage ? 'summary_large_image' : 'summary')
    if (ogImage) upsertMeta('property', 'og:image', absolute(ogImage))

    if (description) {
      upsertMeta('name', 'description', description)
      upsertMeta('property', 'og:description', description)
    }

    removeAlternates()
    const parsed: Record<string, string> = alternatesKey
      ? (JSON.parse(alternatesKey) as Record<string, string>)
      : {}
    for (const [hreflang, path] of Object.entries(parsed)) {
      upsertLink('alternate', absolute(path), hreflang)
    }
  }, [title, description, canonicalPath, alternatesKey, noIndex, ogType, ogImage])
}
